import { MinRelationships } from 'app/Bookings/constants'
import { AutofillForm, sfToTWRelationship } from 'constants/salesforce'
import _ from 'lodash'
import { getNumbersOnlyFromString } from 'utils/common'
import { mergeObjectsWithUniqueProps } from 'utils/common/mergeObjectHelper'

type AutofillData = {
  [key: string]: string | AutofillData | boolean
}

export function extractPatients(lead: SfLead): LeadPatients {
  const patients: PatientDetails[] = []
  let leadResponsibleParty: PatientDetails | null = null

  if (!isSalesforceLead(lead)) {
    return { patients }
  }

  // We will use the address from the lead for all patients since patients don't have their own address.
  const allPatientsAddress = {
    line1: lead.Address?.street || null,
    city: lead.Address?.city || null,
    state: lead.Address?.state || null,
    zipcode: lead.Address?.postalCode || null,
  }

  const caller: PatientDetails = {
    firstName: lead.FirstName || null,
    lastName: lead.LastName || null,
    dateOfBirth: lead.Date_of_Birth__c || null,
    email: lead.Email || null,
    phoneNumber: getNumbersOnlyFromString(lead.Phone) || null,
    address: allPatientsAddress,
  }

  for (let i = 1; i <= 6; i++) {
    const patient: PatientDetails = {
      firstName:
        lead[`Patient_${i}First_Name__c`] ||
        lead[`First_Name_Patient_${i}__c`] ||
        lead[`Patient_${i}_First_Name__c`] ||
        null,
      lastName: lead[`Last_Name_Patient_${i}__c`] || null,
      dateOfBirth: lead[`Date_of_Birth_Patient_${i}__c`] || null,
      email:
        lead[`Email_Address_Patient_${i}__c`] ||
        lead[`Email_Patient_${i}__c`] ||
        null,
      phoneNumber:
        getNumbersOnlyFromString(lead[`Phone_Number_Patient_${i}__c`]) ||
        getNumbersOnlyFromString(lead[`Phone_Patient_${i}__c`]) ||
        null,
      relationship:
        lead[`RP_Relationship_to_Patient_${i}__c`] ||
        (lead[`Patient_${i}_Relationship_to_RP__c`] === 'Other'
          ? lead[`Patient${i}_OtherRelationship__c`]
          : lead[`Patient_${i}_Relationship_to_RP__c`]) ||
        null,
    }

    // Add patient to the array if they have at least one non-null property regardless if they have an id or not
    if (Object.values(patient).some((value) => value !== null)) {
      patient.id = `${lead.Id}-${i}`
      patient.address = allPatientsAddress
      patient.relationship = sfToTWRelationship[patient.relationship]

      patients.push(patient)
    }

    if (lead.Responsible_Party_is__c === `Patient ${i}`) {
      leadResponsibleParty = patient
    }
  }

  // Check if the caller is the responsible party
  if (lead.Responsible_Party_is__c === 'Caller') {
    leadResponsibleParty = caller
  }

  return { patients, caller, leadResponsibleParty }
}

export const getInitialValuesForForm = <TData>(
  formType: AutofillForm,
  lead: SfLead,
  sfCase: SfCase,
  initialValues: TData,
  hasLeadId: boolean,
  hasCaseId?: boolean,
  validator?: (lead?: SfLead, sfCase?: SfCase) => boolean
): TData => {
  let generatedAutofillData = {}
  let overrideAutofillData = true

  if ((hasLeadId && !!lead) || (hasCaseId && !!sfCase)) {
    switch (formType) {
      case AutofillForm.EMAIL:
        generatedAutofillData = getAutofillDataForEmail(lead || sfCase)

        break
      case AutofillForm.RP_FORM:
        overrideAutofillData = false
        generatedAutofillData = getAutofillDataForRpForm(lead)
        break
      case AutofillForm.PATIENT_INFO:
        if (!validator || validator(lead)) {
          generatedAutofillData = getAutofillDataForPatientForm(lead)
        }

        break
      case AutofillForm.INSURANCE:
        overrideAutofillData = false
        generatedAutofillData = getAutofillDataForInsurance(lead)
        break
      default:
        generatedAutofillData = {}
    }
  }

  return mergeObjectsWithUniqueProps(
    initialValues as Record<string, unknown>,
    generatedAutofillData as Record<string, unknown>,
    overrideAutofillData
  ) as TData
}

export const getAutofillDataForEmail = (
  salesforceData?: SfLead | SfCase
): AutofillData => {
  if ('Account' in salesforceData && salesforceData.Account.rp_email__c) {
    return { userEmail: salesforceData.Account.rp_email__c ?? '' }
  } else {
    const { leadResponsibleParty } = extractPatients(salesforceData as SfLead)
    return {
      userEmail: leadResponsibleParty?.email ?? '',
    }
  }
}

export const getAutofillDataForInsurance = (lead: SfLead): AutofillData => {
  return {
    insurance: { memberId: lead.Member_ID__c ?? '' },
  }
}

export const getAutofillDataForRpForm = (lead: SfLead): AutofillData => {
  const { leadResponsibleParty } = extractPatients(lead)
  return {
    firstName: leadResponsibleParty?.firstName ?? '',
    lastName: leadResponsibleParty?.lastName ?? '',
    email: leadResponsibleParty?.email ?? '',
    phoneNumber: leadResponsibleParty?.phoneNumber ?? '',
    ...(!!leadResponsibleParty?.phoneNumber && { phoneNumberValid: true }),
    dob: leadResponsibleParty?.dateOfBirth,
    address: {
      city: leadResponsibleParty?.address?.city ?? '',
      zipcode: leadResponsibleParty?.address?.zipcode ?? '',
      state: leadResponsibleParty?.address?.state ?? '',
      line1: leadResponsibleParty?.address?.line1 ?? '',
      country: 'US',
    },
  }
}

export const getAutofillDataForPatientForm = (lead: SfLead): AutofillData => {
  return {
    isExisting: false,
    firstName: lead.FirstName,
    lastName: lead.LastName,
    email: lead.Email,
    phoneNumber: lead.Phone,
    ...(!!lead.Phone && { phoneNumberValid: true }),
    dateOfBirth: lead.Date_of_Birth__c,
    relationship: MinRelationships.CHILD,
  }
}

export function isSalesforceLead(obj: SfLead): obj is SfLead {
  return (
    obj &&
    typeof obj === 'object' &&
    typeof obj.Id === 'string' &&
    typeof obj.Email === 'string'
  )
}

// Salesforce field ID constants
const SF_FIELD_IDS = {
  COUPLES_MARRIAGE: '1eeb15a7-15b6-43a0-a922-24f4d66b33ae', // UAT fallback
  INDIVIDUAL_COUNSELING: 'fa040321-294c-4d0a-8c05-ac94a3797697',
  FAMILY_COUNSELING: '490a6d65-1a8f-4b5d-894a-59636fad249a',
  MEDICATION_PSYCHIATRY: '500ffdd2-a595-489a-909e-87398f9052c5',
} as const

// Helper to create multiple mappings for the same ID
const createFieldMappings = (
  fields: string[],
  id: string
): { [key: string]: string } => {
  return fields.reduce((acc, field) => {
    acc[field] = id
    return acc
  }, {} as { [key: string]: string })
}

// Get couples/marriage counseling ID (environment-specific)
const getCouplesMarriageId = (): string =>
  process.env.NEXT_PUBLIC_SF_COUPLES_MARRIAGE_ID ||
  SF_FIELD_IDS.COUPLES_MARRIAGE

export const salesforceFieldToIdMap: { [key: string]: string } = {
  // Couples/Marriage Counseling - environment-specific
  ...createFieldMappings(
    ['couplesmarriagecounseling', 'couples'],
    getCouplesMarriageId()
  ),

  // Individual Counseling
  ...createFieldMappings(
    [
      'individualcounseling',
      'individualtherapy',
      'individualtherapyadult',
      'childadolescenttherapy',
    ],
    SF_FIELD_IDS.INDIVIDUAL_COUNSELING
  ),

  // Family Counseling
  ...createFieldMappings(
    ['familycounseling', 'family'],
    SF_FIELD_IDS.FAMILY_COUNSELING
  ),

  // Medication/Psychiatry
  ...createFieldMappings(
    [
      'medicationpsychiatry',
      'psychiatryadult',
      'adolescentpsychiatry1417',
      'adolescentpsychiatry',
    ],
    SF_FIELD_IDS.MEDICATION_PSYCHIATRY
  ),
}

function normalizeSalesforceField(field: string): string {
  return field
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

// Separator Salesforce uses between selected values of a multi-select picklist
const SF_MULTI_SELECT_SEPARATOR = ';'

/**
 * Maps a Salesforce `Service_type__c` value to a counseling type filter id.
 *
 * `Service_type__c` is a multi-select picklist (e.g.
 * "Individual Therapy (Adult);Psychiatry (Adult)"), but provider search only
 * supports one counseling type. Returns the id of the first selected value
 * that maps, and falls back to Individual Counseling when none map (e.g.
 * "Other") so callers never put a non-UUID into the `counselingType` filter.
 * Returns undefined only when the field is empty.
 */
export function getFilterIdFromSalesforceField(
  field: string
): string | undefined {
  if (!field) {
    return
  }

  const matchedId = field
    .split(SF_MULTI_SELECT_SEPARATOR)
    .map((value) => salesforceFieldToIdMap[normalizeSalesforceField(value)])
    .find((id) => !!id)

  return matchedId ?? SF_FIELD_IDS.INDIVIDUAL_COUNSELING
}
